import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography, Table, Space, Button, Tag, App, Card, Row, Col, Tooltip,
  Input, Switch, Spin, Result, Empty,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import {
  Report, ReportServerDetail, StatusBreakdown,
  STATE_TAG_COLORS,
  StatusBar, StatusTags,
  hasProblem, serverWorstRank, worstState,
} from '../components/reportVisuals';
import ReportItemsTable from '../components/ReportItemsTable';
import { formatDateTime, localTimeZone } from '../utils/datetime';

const { Title, Text } = Typography;

// A report is every server with its components, steps and per-target attempts.
// At ~250 servers that is not a modal: it cannot be linked to, cannot be
// filtered, and scrolls past anything you were looking for. It is a page, with
// an address, opening on the rows that are actually broken.

// searchHaystack is everything one row can be found by. Operators arrive here
// from an alert holding one of: a server name, an IP, the integrator's own id,
// a proxy endpoint, or a modem label — so all of them match, and none of them
// requires knowing which field it was.
function searchHaystack(s: ReportServerDetail): string {
  const parts = [
    s.serverName, s.serverKey, s.mainIP, s.country,
    s.sourceKey, s.sourceId,
  ];
  for (const i of s.items || []) {
    parts.push(i.ip, i.protocol, i.label);
  }
  return parts.filter(Boolean).join(' ').toLowerCase();
}

const ReportDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Opens filtered to problems. A report exists to answer "what is broken" —
  // the healthy rows are the part you already know.
  const [problemsOnly, setProblemsOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchReport = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/reports/${id}`);
      setReport(data.data);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        message.error('Failed to load report');
      }
    } finally {
      setLoading(false);
    }
  }, [id, message]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportReport = async (format: 'csv' | 'json') => {
    if (!id) return;
    try {
      const { data } = await apiClient.get(`/reports/${id}/export?format=${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${id}.${format}`;
      link.click();
    } catch {
      message.error('Failed to export report');
    }
  };

  const allServers = useMemo(() => report?.servers || [], [report]);

  // The three counts the header shows. They are computed together because they
  // only mean anything next to each other: "42 servers" is unreadable without
  // knowing whether that is all of them or what a filter left.
  const counts = useMemo(() => {
    const problem = allServers.filter(hasProblem).length;
    return { total: allServers.length, problem };
  }, [allServers]);

  const visibleServers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allServers.filter((s) => {
      if (problemsOnly && !hasProblem(s)) return false;
      if (q && !searchHaystack(s).includes(q)) return false;
      return true;
    });
    // Worst first: the reason to open a report is at the top of it.
    return [...filtered].sort((a, b) => {
      const d = serverWorstRank(b) - serverWorstRank(a);
      if (d !== 0) return d;
      return (a.serverName || a.serverKey).localeCompare(b.serverName || b.serverKey);
    });
  }, [allServers, problemsOnly, search]);

  useEffect(() => { setPage(1); }, [search, problemsOnly]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !report) {
    return (
      <Result
        status="404"
        title="Report not found"
        subTitle="It may have been deleted by the retention limit or by a data reset."
        extra={<Button type="primary" onClick={() => navigate('/reports')}>Back to reports</Button>}
      />
    );
  }

  const serverColumns: ColumnsType<ReportServerDetail> = [
    {
      title: 'Server', key: 'server', ellipsis: { showTitle: false },
      render: (_: unknown, r: ReportServerDetail) => {
        // Deleted servers route to the snapshot archive page; live servers go
        // to the health detail page. Archived entries get a small tag so the
        // operator notices the difference before clicking.
        const target = r.deleted
          ? `/servers/${r.serverKey}/snapshot`
          : `/servers/${r.serverKey}/health`;
        return (
          <Tooltip title={r.serverName ? `${r.serverName} · ${r.serverKey}` : r.serverKey}>
            <span style={{ whiteSpace: 'nowrap' }}>
              <Link to={target} onClick={(e) => e.stopPropagation()}>
                {r.serverName || r.serverKey}
              </Link>
              {r.deleted && (
                <Tag color="default" style={{ margin: '0 0 0 4px', fontSize: 12, padding: '0 4px' }}>archived</Tag>
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Main IP', dataIndex: 'mainIP', key: 'mainIP', width: 130,
      ellipsis: { showTitle: false },
      render: (ip: string) => <Tooltip title={ip}><span style={{ fontSize: 12 }}>{ip}</span></Tooltip>,
    },
    {
      title: 'Country', dataIndex: 'country', key: 'country', width: 74,
      render: (code: string) => {
        if (!code) return <Text type="secondary">--</Text>;
        return (
          <Tooltip title={code}>
            <img
              src={`https://flagcdn.com/20x15/${code.toLowerCase()}.png`}
              width={20} height={15} alt={code}
              style={{ verticalAlign: 'middle', marginRight: 4 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span style={{ fontSize: 12 }}>{code}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Source', key: 'source', width: 120, ellipsis: { showTitle: false },
      render: (_: unknown, r: ReportServerDetail) => {
        if (!r.sourceKey) return <Text type="secondary" style={{ fontSize: 12 }}>Manual</Text>;
        const label = <Text code style={{ fontSize: 12 }}>{r.sourceKey}</Text>;
        const tip = r.sourceId ? `${r.sourceKey} · ${r.sourceId}` : r.sourceKey;
        return (
          <Tooltip title={tip}>
            {r.sourceUrl
              ? <a href={r.sourceUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{label}</a>
              : label}
          </Tooltip>
        );
      },
    },
    {
      title: 'Networks', key: 'netState', width: 112,
      render: (_: unknown, r: ReportServerDetail) => {
        const nets = (r.items || []).filter((i) => i.type === 'ip');
        if (nets.length === 0) return <Text type="secondary">--</Text>;
        const worst = worstState(nets);
        return <Tag color={STATE_TAG_COLORS[worst] || 'default'} style={{ margin: 0 }}>{worst} ({nets.length})</Tag>;
      },
    },
    {
      title: 'Proxies', key: 'proxyState', width: 112,
      render: (_: unknown, r: ReportServerDetail) => {
        const prx = (r.items || []).filter((i) => i.type === 'proxy');
        if (prx.length === 0) return <Text type="secondary">--</Text>;
        const worst = worstState(prx);
        return <Tag color={STATE_TAG_COLORS[worst] || 'default'} style={{ margin: 0 }}>{worst} ({prx.length})</Tag>;
      },
    },
  ];

  // The two breakdown tables show the distribution as a bar rather than a wrap
  // of tags: a tag row costs two or three lines per country in a half-width
  // card, the bar costs one and carries every count in its tooltip.
  const countryColumns: ColumnsType<{ country: string; networks: StatusBreakdown; proxies: StatusBreakdown }> = [
    { title: 'Country', dataIndex: 'country', key: 'country', width: 92, ellipsis: true },
    {
      title: 'Networks', key: 'networks',
      render: (_: unknown, r) => <StatusBar breakdown={r.networks} />,
    },
    {
      title: 'Proxies', key: 'proxies',
      render: (_: unknown, r) => <StatusBar breakdown={r.proxies} />,
    },
  ];

  const sourceColumns: ColumnsType<{ source: string; networks: StatusBreakdown; proxies: StatusBreakdown }> = [
    {
      title: 'Source', dataIndex: 'source', key: 'source', width: 120, ellipsis: { showTitle: false },
      render: (s: string) => s
        ? <Tooltip title={s}><Text code style={{ fontSize: 12 }}>{s}</Text></Tooltip>
        : <Text type="secondary" style={{ fontSize: 12 }}>Manual</Text>,
    },
    {
      title: 'Networks', key: 'networks',
      render: (_: unknown, r) => <StatusBar breakdown={r.networks} />,
    },
    {
      title: 'Proxies', key: 'proxies',
      render: (_: unknown, r) => <StatusBar breakdown={r.proxies} />,
    },
  ];

  return (
    <>
      {/* Back, title, timezone note and both exports on one line: the page
          used to spend three lines saying what it is before showing any of it. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Reports</Button>
        <Title level={4} style={{ margin: 0 }}>
          Report — {formatDateTime(report.generatedAt || report.date, report.date)}
        </Title>
        {/* Every time on this page is the reader's own clock, and the report
            is read by people in several of them — so the page says whose. */}
        <Text type="secondary" style={{ fontSize: 12 }}>times in {localTimeZone()}</Text>
        <div style={{ flex: 1 }} />
        <Button size="small" icon={<DownloadOutlined />} onClick={() => exportReport('csv')}>CSV</Button>
        <Button size="small" icon={<DownloadOutlined />} onClick={() => exportReport('json')}>JSON</Button>
      </div>

      {/* One strip instead of two stacked cards — two bars and their counts are
          two lines of content, not 140px of card chrome. */}
      <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '8px 12px' } }}>
        <Row gutter={16}>
          {([['Networks', report.summary?.networks], ['Proxies', report.summary?.proxies]] as const).map(([label, breakdown]) => (
            <Col xs={24} lg={12} key={label}>
              {/* Label, bar and counts on one line: the tags used to take a
                  second line of their own under a bar that had room beside it. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text strong style={{ fontSize: 12, width: 62, flex: 'none' }}>{label}</Text>
                <div style={{ flex: 1, minWidth: 80 }}><StatusBar breakdown={breakdown} /></div>
                <div style={{ flex: 'none' }}><StatusTags breakdown={breakdown} /></div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      <Card
        size="small"
        style={{ marginBottom: 12 }}
        styles={{ body: { padding: '0 8px 8px' } }}
        title={
          <Space wrap size={6}>
            <span style={{ fontSize: 14 }}>Server Details</span>
            {/* The counter is not decoration: with a filter on by default, a
                bare row count cannot be told apart from "that is all there
                is". Showing all three numbers makes the filter's effect
                visible without turning it off. */}
            <Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
              showing {visibleServers.length} · {counts.problem} with problems · {counts.total} total
            </Text>
          </Space>
        }
        extra={
          <Space wrap size={8}>
            <Input.Search
              allowClear
              size="small"
              placeholder="server, IP, sourceId, host:port, modem"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 230 }}
            />
            <Space size={6}>
              <Switch size="small" checked={problemsOnly} onChange={setProblemsOnly} />
              <Text style={{ fontSize: 12 }}>Problems only</Text>
            </Space>
          </Space>
        }
      >
        <Table
          dataSource={visibleServers}
          rowKey="serverKey"
          size="small"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  search
                    ? `Nothing matches "${search}"${problemsOnly ? ' among servers with problems' : ''}.`
                    : problemsOnly
                      ? 'No server in this report has a problem.'
                      : 'This report has no server detail.'
                }
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            size: 'small',
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
          }}
          expandable={{
            expandedRowRender: (record: ReportServerDetail) => {
              const netItems = (record.items || []).filter((i) => i.type === 'ip');
              const proxyItems = (record.items || []).filter((i) => i.type === 'proxy');
              return (
                <div style={{ padding: '2px 0' }}>
                  {netItems.length > 0 && (
                    <div style={{ marginBottom: 6 }}>
                      <Text strong style={{ fontSize: 12, display: 'block' }}>Networks</Text>
                      <ReportItemsTable items={netItems} />
                    </div>
                  )}
                  {proxyItems.length > 0 && (
                    <div>
                      <Text strong style={{ fontSize: 12, display: 'block' }}>Proxies</Text>
                      <ReportItemsTable items={proxyItems} />
                    </div>
                  )}
                </div>
              );
            },
          }}
          columns={serverColumns}
        />
      </Card>

      {/* Breakdowns sit side by side, each scrolling on its own. They are
          reference tables read against each other ("which country, which
          source, which error"), so stacking them full-height would push the
          third one off the screen. */}
      <Row gutter={12}>
        <Col xs={24} xl={8} style={{ marginBottom: 12 }}>
          <Card size="small" title={<span style={{ fontSize: 14 }}>Country Breakdown</span>} styles={{ body: { maxHeight: 300, overflow: 'auto', padding: '0 8px 8px' } }}>
            <Table
              dataSource={report.countries}
              rowKey="country"
              size="small"
              pagination={false}
              columns={countryColumns}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8} style={{ marginBottom: 12 }}>
          <Card size="small" title={<span style={{ fontSize: 14 }}>Source Breakdown</span>} styles={{ body: { maxHeight: 300, overflow: 'auto', padding: '0 8px 8px' } }}>
            <Table
              dataSource={report.sources}
              rowKey={(r) => r.source || '__manual__'}
              size="small"
              pagination={false}
              columns={sourceColumns}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sources" /> }}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8} style={{ marginBottom: 12 }}>
          <Card size="small" title={<span style={{ fontSize: 14 }}>Top Issues</span>} styles={{ body: { maxHeight: 300, overflow: 'auto', padding: '0 8px 8px' } }}>
            <Table
              dataSource={report.topIssues}
              rowKey={(r) => r.errorType + r.country}
              size="small"
              pagination={false}
              columns={[
                { title: 'Error Type', dataIndex: 'errorType', ellipsis: true },
                { title: 'Country', dataIndex: 'country', width: 96, ellipsis: true },
                { title: 'Count', dataIndex: 'count', width: 64, align: 'right' as const },
              ]}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No issues" /> }}
            />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              Long-term failures (STALE) are excluded here so chronic dead targets don't crowd out
              fresh problems; they are still listed in the server table above.
            </Text>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ReportDetailPage;
